import React from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import type { GroupBase, StylesConfig } from "react-select";

export interface IOption {
  value: string;
  label: string;
}

export interface LoadOptionsAdditional {
  page: number;
}

export interface LoadOptionsResult {
  options: IOption[];
  hasMore: boolean;
  additional: LoadOptionsAdditional;
}

export type LoadOptionsFn = (
  search: string,
  loadedOptions: unknown,
  additional?: LoadOptionsAdditional,
) => Promise<LoadOptionsResult>;

interface AsyncSearchSelectProps {
  value: IOption | null;
  onChange: (option: IOption | null) => void;
  loadOptions: LoadOptionsFn;
  placeholder?: string;
  isDisabled?: boolean;
  hasError?: boolean;
  isClearable?: boolean;
  pageSize?: number;
  /** Bump this to force a fresh fetch (e.g. when a dependent filter changes) */
  cacheKey?: string | number;
}

// Styled to match the app's existing `inputClass` look (rounded-lg, gray-200
// border, primary focus ring) so it sits naturally next to plain <input>s.
const buildStyles = (
  hasError?: boolean,
): StylesConfig<IOption, false, GroupBase<IOption>> => ({
  control: (base, state) => ({
    ...base,
    minHeight: "42px",
    borderRadius: "0.5rem", // rounded-lg
    borderColor: hasError
      ? "#fca5a5" // red-300
      : state.isFocused
        ? "var(--primary, #6366f1)"
        : "#e5e7eb", // gray-200
    boxShadow: state.isFocused
      ? `0 0 0 4px ${hasError ? "rgba(248,113,113,0.15)" : "rgba(99,102,241,0.10)"}`
      : "none",
    fontSize: "0.875rem", // text-sm
    transition: "all 150ms ease",
    "&:hover": {
      borderColor: hasError ? "#fca5a5" : "var(--primary, #6366f1)",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 12px",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af", // gray-400
  }),
  singleValue: (base) => ({
    ...base,
    color: "#111827", // gray-900
  }),
  menu: (base) => ({
    ...base,
    borderRadius: "0.5rem",
    overflow: "hidden",
    boxShadow:
      "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)",
    border: "1px solid #f3f4f6",
  }),
  // When menuPortalTarget is set, the menu is teleported out to
  // document.body and it's THIS wrapper — not `menu` above — that controls
  // stacking there. React-select's default zIndex on the portal is 1, which
  // can sit behind other fixed/positioned page content, so the menu renders
  // (and the data loads fine) but is invisible. This was the actual bug.
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuList: (base) => ({
    ...base,
    padding: "4px",
  }),
  option: (base, state) => ({
    ...base,
    borderRadius: "0.375rem",
    fontSize: "0.875rem",
    padding: "8px 10px",
    backgroundColor: state.isSelected
      ? "var(--primary, #6366f1)"
      : state.isFocused
        ? "#f5f5ff"
        : "white",
    color: state.isSelected ? "white" : "#111827",
    cursor: "pointer",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "#9ca3af",
    padding: "0 8px",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "#9ca3af",
    padding: "0 4px",
    "&:hover": { color: "#ef4444" },
  }),
  loadingMessage: (base) => ({ ...base, fontSize: "0.8125rem" }),
  noOptionsMessage: (base) => ({ ...base, fontSize: "0.8125rem" }),
  input: (base) => ({ ...base, fontSize: "0.875rem" }),
});

const AsyncSearchSelect: React.FC<AsyncSearchSelectProps> = ({
  value,
  onChange,
  loadOptions,
  placeholder = "Search…",
  isDisabled,
  hasError,
  isClearable = true,
  pageSize = 20,
  cacheKey,
}) => {
  return (
    <AsyncPaginate
      value={value}
      onChange={(option) => onChange(option as IOption | null)}
      loadOptions={loadOptions}
      additional={{ page: 1 }}
      debounceTimeout={350}
      isClearable={isClearable}
      isDisabled={isDisabled}
      placeholder={placeholder}
      styles={buildStyles(hasError)}
      cacheUniqs={[pageSize, cacheKey]}
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      menuPosition="fixed"
      noOptionsMessage={({ inputValue }) =>
        inputValue ? "No matches found" : "No options"
      }
      loadOptionsOnMenuOpen
      openMenuOnFocus
    />
  );
};

export default AsyncSearchSelect;